import { Button } from '@/components/ui/button';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import GolfIcon from '@/components/GolfIcon';
import { t } from 'i18next';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IonModal, IonContent, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonFooter } from '@ionic/react';

type ChatInfoPanelProps = {
  chatDetails: {
    id: string;
    name?: string;
    type?: 'direct' | 'group';
    imageUrl?: string;
    createdBy?: string;
  } | null;
  participants: Array<{
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
    online?: boolean;
  }>;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onDeleteChat: () => void;
};

export const ChatInfoPanel: React.FC<ChatInfoPanelProps> = ({
  chatDetails,
  participants,
  userId,
  isOpen,
  onClose,
  onDeleteChat,
}) => {
  const history = useHistory();

  if (!chatDetails) return null;

  const handleViewProfile = (participantId: string) => {
    history.push(`/profile/${participantId}`);
  };

  // Get the other participant in a direct chat
  const getOtherParticipant = () => {
    if (chatDetails.type === 'direct' && participants.length > 0) {
      return participants.find(p => p.id !== userId) || participants[0];
    }
    return null;
  };

  // Get display name based on chat type
  const getChatDisplayName = () => {
    if (chatDetails.type === 'group') {
      // For group chats, use the chat name
      return chatDetails.name || 'Group Chat';
    } else {
      // For direct chats, use the other participant's name
      const otherParticipant = getOtherParticipant();
      if (otherParticipant) {
        return `${otherParticipant.firstName} ${otherParticipant.lastName}`;
      }
      return chatDetails.name || 'Chat';
    }
  };

  // Get avatar image based on chat type
  const getChatAvatarUrl = () => {
    if (chatDetails.type === 'group') {
      // For group chats, use the chat image
      return chatDetails.imageUrl;
    } else {
      // For direct chats, use the other participant's image
      const otherParticipant = getOtherParticipant();
      return otherParticipant?.imageUrl || chatDetails.imageUrl;
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="chat-info-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('informations')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <GolfIcon name="close" className="size-5" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">

        <div className="flex flex-col items-center p-4">
          <GolfAvatar className="size-20" imageUrl={getChatAvatarUrl()} />
          {chatDetails.type === 'group' && (
            <p className="text-sm text-muted-foreground">
              {participants.length} {t('members')}
            </p>
          )}
          <h2 className="mt-2 text-lg font-bold">{getChatDisplayName()}</h2>
          {chatDetails.type === 'group' && (
            <p className="text-sm text-muted-foreground">
              {participants.length} {t('members')}
            </p>
          )}
        </div>

        {participants.length > 0 && (
          <div className="flex-1 overflow-y-auto p-4">
            <h4 className="mb-2 font-semibold">{t('members')}</h4>
            <div className="space-y-2">
              {participants.map((participant) => (
                <div key={participant.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GolfAvatar className="size-8" imageUrl={participant.imageUrl} />
                    <div className="flex items-center">
                      <span className="font-medium">{participant.firstName + ' ' + participant.lastName}</span>
                      {chatDetails.createdBy === participant.id && (
                        <GolfIcon name="crown" className="ml-1 size-4 text-amber-500" />
                      )}
                      <div
                        className={`ml-2 size-2 rounded-full ${participant.online ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                      />
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      onClick={() => handleViewProfile(participant.id)}
                    >
                      <GolfIcon name="link-square" className="size-4" />
                    </Button>
                    {chatDetails.type === 'group' &&
                      chatDetails.createdBy === userId &&
                      participant.id !== userId && (
                        <Button variant="ghost" size="icon" className="size-7 text-destructive">
                          <GolfIcon name="ban" className="size-4" />
                        </Button>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <Button
            variant="destructive"
            className="w-full"
            onClick={onDeleteChat}
          >
            {t('delete_chat')}
          </Button>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};
